import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AuthService from '../../services/authService';
import { API_BASE } from '../../config/api';

/**
 * Privacy-gated child avatar.
 *
 * Falls back to the emoji avatar when:
 *   - no photo has been uploaded (`photoUrl` is falsy)
 *   - the photo fetch fails (403 = no longer allowlisted, 404 = removed)
 *
 * The blob URL is created from a fetch() with auth headers; can't use a
 * plain `<img src>` because the `/api/child-photos/:childId` endpoint
 * requires a Bearer token. Object URL is revoked on unmount / photoUrl
 * change so we don't leak memory.
 *
 * Whenever a new surface needs to render a child photo (WhosGoingModal,
 * trip planner cards, social feed, etc.) — use this component. Do NOT
 * reinvent the fetch+blob pattern inline.
 *
 * @param {object} props
 * @param {string} props.photoUrl  relative or absolute URL; e.g.
 *                                 '/api/child-photos/child_abc?v=123'
 * @param {string} props.emoji     fallback emoji when no photo is available
 * @param {'xs'|'face'|'sm'|'md'|'lg'|'xl'} [props.size='md']
 * @param {string} [props.altName] used for the img alt text + aria-label
 */
function ChildAvatar({ photoUrl, emoji, size = 'md', altName = '' }) {
  const { t } = useTranslation();
  // xs and face match UserAvatar's sm (28px) and lg (56px), so a child sits
  // beside adults at the same size (WhosGoingModal's chips and face grid).
  const sizePx = {
    xs: 'w-7 h-7 text-lg',
    face: 'w-14 h-14 text-4xl',
    sm: 'w-8 h-8 text-2xl',
    md: 'w-12 h-12 text-3xl',
    lg: 'w-20 h-20 text-5xl',
    xl: 'w-24 h-24 text-6xl'
  }[size] || 'w-12 h-12 text-3xl';

  const [blobUrl, setBlobUrl] = useState(null);
  const currentObjectUrl = useRef(null);

  useEffect(() => {
    if (!photoUrl) {
      setBlobUrl(null);
      return undefined;
    }
    let cancelled = false;
    const fullUrl = photoUrl.startsWith('http') ? photoUrl : `${API_BASE}${photoUrl}`;
    fetch(fullUrl, { headers: AuthService.getAuthHeaders() })
      .then(r => (r.ok ? r.blob() : null))
      .then(blob => {
        if (cancelled || !blob) return;
        const url = URL.createObjectURL(blob);
        currentObjectUrl.current = url;
        setBlobUrl(url);
      })
      .catch(() => { /* fall through to emoji */ });

    return () => {
      cancelled = true;
      if (currentObjectUrl.current) {
        URL.revokeObjectURL(currentObjectUrl.current);
        currentObjectUrl.current = null;
      }
    };
  }, [photoUrl]);

  if (blobUrl) {
    return (
      <img
        src={blobUrl}
        alt={altName ? t('childAvatar.photoAlt', { name: altName }) : ''}
        className={`${sizePx} rounded-full object-cover flex-shrink-0`}
      />
    );
  }
  return (
    <span
      className={`${sizePx} rounded-full bg-background flex items-center justify-center flex-shrink-0 leading-none`}
      aria-label={altName ? t('childAvatar.avatarAriaLabel', { name: altName }) : t('childAvatar.defaultAriaLabel')}
    >
      {emoji || '👶'}
    </span>
  );
}

export default ChildAvatar;
